import React from 'react';
import GridFilterItem from '../../../Base/Components/GridFilter/GridFilterItem';
import { GridFilterItemType } from '../../../Base/Enums/GridFilterItemType.enum';
import GridFilter from '../../../Base/Components/GridFilter/GridFilter';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import useFilters from '../../../Base/Hooks/useFilters';
import { GRID_FILTER_KEYS } from '../../../Base/Components/GridFilter/gridFilterKeys';
import { PaginationOrderHow } from '../../../../graphql/graphql';
import GridFilterOrderItem from '../../../Base/Components/GridFilter/GridFilterOrderItem';
import useTranslation from '../../../../Hooks/useTranslation';

export interface MonitoringCenterAllRequestsFilterValues {
	caseTypeIds?: string[];
	priorityId?: string;
	patient?: string;
	date?: string;
	caseStatusValue?: string;
	doctorId?: string;
	attendingDoctorId?: string;
	workplaceId?: string;
	createdAt?: string;
	code?: string;
	caseTypeCode?: string;
	createdAtRange?: {
		from: string;
		to: string;
	};
	order?: {
		key: string;
		how: PaginationOrderHow;
	};
}

interface Props {
	onFilter: (values: MonitoringCenterAllRequestsFilterValues) => void;
}

const MonitoringCenterAllRequestsFilter: React.FC<Props> = ({ onFilter }) => {
	const {
		handleSelectValue,
		handleDateRange,
		checkEmptyString,
		isDateRangeVisible,
		handleDateRangeInPicker,
		handleDateRangeVisibility,
	} = useFilters();
	const { t } = useTranslation();

	const ORDER_OPTIONS: SelectValue[] = [
		{
			value: {
				key: 'cases_sorted_by_entry.created_at',
				how: PaginationOrderHow.Desc,
			},
			label: t('component.gridFilterOrdering.fromNewest') ?? '',
		},
		{
			value: {
				key: 'cases_sorted_by_entry.created_at',
				how: PaginationOrderHow.Asc,
			},
			label: t('component.gridFilterOrdering.fromOldest') ?? '',
		},
	];

	const onFilterData = (values: any) => {
		handleDateRangeVisibility(values?.createdAt?.value);
		const filter: MonitoringCenterAllRequestsFilterValues = {
			caseTypeIds: values?.caseTypeIds?.length
				? values.caseTypeIds.map((item: SelectValue) => item.value)
				: undefined,
			priorityId: values?.priorityId?.value || undefined,
			patient: checkEmptyString(values?.patient),
			createdAt: handleDateRangeInPicker(values?.createdAt?.value),
			caseStatusValue: values?.caseStatus?.value,
			attendingDoctorId: handleSelectValue(values?.attendingDoctor?.value),
			doctorId: handleSelectValue(values?.assignedDoctor?.value),
			workplaceId: handleSelectValue(values?.workplaceId?.value),
			code: values?.code || undefined,
			caseTypeCode: values?.caseTypeCode?.value,
			createdAtRange: handleDateRange(values?.dateFrom, values?.dateTo),
			order: values?.order?.value,
		};
		onFilter(filter);
	};

	return (
		<GridFilter
			onChange={onFilterData}
			storageKey={GRID_FILTER_KEYS.MONITORING_CENTER_ALL_REQUESTS_FILTER}>
			<GridFilterItem
				type={GridFilterItemType.ROOT_CASE_TYPE_CODE}
				width={340}
				enableAutocomplete
			/>
			<GridFilterItem
				type={GridFilterItemType.CASE_PRIORITY}
				width={280}
				enableAutocomplete
			/>
			<GridFilterItem
				type={GridFilterItemType.FULLTEXT}
				customKey='patient'
				width={330}
				customPlaceholder='base.startTypingSurname'
				customLabel='base.shared.patient'
				enableAutocomplete
			/>
			<GridFilterItem
				customLabel='component.monitoringCenterAllRequestsFilter.state'
				type={GridFilterItemType.CASE_STATUS}
				width={190}
				customPlaceholder='base.shared.pick'
				enableAutocomplete
			/>
			<GridFilterItem
				customLabel='component.monitoringCenterAllRequestsFilter.createdAt'
				type={GridFilterItemType.CASE_CREATE_DAY}
				width={220}
				customPlaceholder='base.shared.pick'
				enableAutocomplete
			/>
			<GridFilterItem
				type={GridFilterItemType.DATE_FROM}
				width={170}
				customLabel='base.shared.from'
				enableAutocomplete
				visible={isDateRangeVisible}
			/>
			<GridFilterItem
				type={GridFilterItemType.DATE_TO}
				width={170}
				customLabel='base.shared.to'
				enableAutocomplete
				visible={isDateRangeVisible}
			/>
			<GridFilterItem
				type={GridFilterItemType.CASE_CODE}
				width={220}
				enableAutocomplete
			/>
			<GridFilterItem
				type={GridFilterItemType.DOCTOR_ID_FROM_ALL_WORKPLACES}
				width={330}
				customLabel='base.shared.attendingDoctor'
				customPlaceholder='base.startTypingSurname'
				customKey='attendingDoctor'
				enableAutocomplete
			/>
			<GridFilterItem
				type={GridFilterItemType.MEDIC_ID_FROM_ALL_WORKPLACES}
				width={330}
				customLabel='base.assigment'
				customPlaceholder='base.startTypingSurname'
				customKey='assignedDoctor'
				enableAutocomplete
			/>
			<GridFilterItem
				type={GridFilterItemType.WORKPLACE}
				width={330}
				enableAutocomplete
			/>
			<GridFilterOrderItem
				orderOptions={ORDER_OPTIONS}
				width={300}
				enableAutocomplete
			/>
		</GridFilter>
	);
};

export default MonitoringCenterAllRequestsFilter;
